import React, { useState, useEffect } from 'react';
import { accountsAPI } from '../lib/api';
import {
  TrendingUp, TrendingDown, Target, AlertTriangle, Clock,
  ChevronDown, ChevronUp, Activity, DollarSign, BarChart3,
  ArrowRight, Shield, Zap, Calendar, Layers, MessageSquare,
  PieChart, ArrowDown, ArrowUp
} from 'lucide-react';

const fmt = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '\u20b90';
  const abs = Math.abs(v);
  if (abs >= 100000) return `\u20b9${(v / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `\u20b9${(v / 1000).toFixed(0)}k`;
  return `\u20b9${v.toFixed(0)}`;
};

const statusConfig = {
  ACHIEVED: { label: 'Achieved', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: TrendingUp },
  ON_TRACK: { label: 'On Track', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: TrendingUp },
  SOFT: { label: 'Soft', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', icon: TrendingDown },
  AT_RISK: { label: 'At Risk', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', icon: AlertTriangle },
  RECOVERABLE: { label: 'Recoverable', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', icon: Activity },
  PROJECTION_ONLY: { label: 'Projection Only', color: 'text-zinc-300', bg: 'bg-zinc-500/10 border-zinc-500/20', icon: Target },
};

const gradeConfig = {
  ACTIONABLE: { label: 'Actionable', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  DIRECTIONAL: { label: 'Directional', color: 'text-amber-400', bg: 'bg-amber-500/15' },
  INSUFFICIENT: { label: 'Insufficient Data', color: 'text-zinc-500', bg: 'bg-zinc-500/15' },
};

const cashConfig = {
  STABLE: { color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  WATCH: { color: 'text-amber-400', bg: 'bg-amber-500/10' },
  CRITICAL: { color: 'text-red-400', bg: 'bg-red-500/10' },
};

const confColor = (score) => {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 55) return 'text-amber-400';
  return 'text-red-400';
};

// Truth Contract gating messages — surfaced when upstream P&L truth blocks
// margin/cost derivatives. Sales projection continues regardless; only
// cost/margin/cash layers are suppressed.
const TRUTH_GATE_COPY = {
  INVALID: {
    title: 'Cost-side forecast suppressed — P&L invalid',
    detail: 'Margin, expense, and cash forecasts are unreliable until COGS/inventory issues are resolved. Sales pace below is still live.',
    tone: 'border-rose-500/40 bg-rose-500/8 text-rose-200',
    iconTone: 'text-rose-400',
  },
  NOT_ACTIONABLE: {
    title: 'Cost-side forecast directional',
    detail: 'Cost layers are based on proxy data and not decision-grade. Sales pace below is still live.',
    tone: 'border-amber-500/40 bg-amber-500/8 text-amber-200',
    iconTone: 'text-amber-400',
  },
  DIRECTIONAL: {
    title: 'Directional forecast',
    detail: 'Some layers are estimated from proxy inputs — treat as a trend, not a precise number.',
    tone: 'border-amber-500/25 bg-amber-500/5 text-amber-200',
    iconTone: 'text-amber-300',
  },
};

export const ForecastCard = ({ dateFrom, dateTo, truthContract }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForecast = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await accountsAPI.getPnLForecast({ as_of_date: dateTo || dateFrom });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load forecast');
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-5 animate-pulse" data-testid="forecast-card-loading">
        <div className="h-5 w-40 bg-zinc-800 rounded mb-3" />
        <div className="h-8 w-24 bg-zinc-800 rounded mb-2" />
        <div className="h-4 w-full bg-zinc-800 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-rose-500/5 border border-rose-500/30 rounded-lg p-4 flex items-center justify-between gap-3"
        data-testid="forecast-card-error"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-rose-200 font-medium">Month forecast unavailable</p>
            <p className="text-[11px] text-rose-300/80 truncate">{String(error)}</p>
          </div>
        </div>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            accountsAPI
              .getPnLForecast({ as_of_date: dateTo || dateFrom })
              .then((res) => setData(res.data))
              .catch((err) => setError(err.response?.data?.detail || 'Failed to load forecast'))
              .finally(() => setLoading(false));
          }}
          className="text-xs px-2.5 py-1 rounded border border-rose-500/40 text-rose-200 hover:bg-rose-500/10 shrink-0"
          data-testid="forecast-card-retry-btn"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty/unexpected payload — keep a lightweight inline notice instead of
  // silently rendering nothing. A blank slot in the P&L page used to look
  // identical to a deployment regression.
  if (!data) {
    return (
      <div
        className="bg-zinc-900/60 border border-zinc-800/60 border-dashed rounded-lg p-4 flex items-center gap-2.5"
        data-testid="forecast-card-empty"
      >
        <Clock className="w-4 h-4 text-zinc-500 shrink-0" />
        <p className="text-xs text-zinc-500">
          Month forecast not yet available for this range.
        </p>
      </div>
    );
  }

  const { landing, target_gap, recovery, short_horizon, mix_forecast, cash_signal,
          expense_layer, margin_layer, cash_layer, layer_confidence, explanations,
          drivers, recommended_action, forecast_delta, confidence, period, vs_last_month,
          actual_mtd, projected_remaining, forecast_final,
          implied_daily_required, historical_daily_max, recent_daily_avg,
          forecast_components, forecast_weights, plausibility,
          truth_flags, decision_grade, decision_score, forecast_layers } = data;

  // Backend emits truth_flags — prefer them over parent pnlData.truth_contract
  // so the forecast layer gates remain self-contained. cogs/cash flags here
  // are authoritative for the layered view; parent truth_contract is still
  // used for the global suppression banner.
  const cogsActionable = truth_flags?.cogs_actionable !== false;
  const cashActionable = truth_flags?.cash_actionable !== false;

  // Decision grade (min of data/forecast confidence) wins over the prettier
  // forecast_grade for the visible badge. This is the 1OS rule: show the
  // weaker decision confidence, not the prettier model confidence.
  const effectiveGradeKey = decision_grade || confidence?.forecast_grade || 'INSUFFICIENT';
  const status = statusConfig[landing?.status] || statusConfig.SOFT;
  const grade = gradeConfig[effectiveGradeKey] || gradeConfig.INSUFFICIENT;
  const cash = cashConfig[cash_signal?.status] || cashConfig.STABLE;
  const StatusIcon = status.icon;

  // Layered-truth gating (1OS-grade): the sales layer is its own truth
  // contract. The previous binary gate (`forecast_grade === 'INSUFFICIENT'`)
  // collapsed a 4-layer backend contract — sales / margin / cash / expense —
  // into a single hide-everything switch. That hid the projected sales
  // amount, the target-hit date, and the required daily even when the
  // backend explicitly marked the sales layer VALID + can_render=true.
  //
  // New rule: hide sales-only renders ONLY when the sales layer itself is
  // blocked. Margin / cash / expense suppression continues to use their own
  // truth_flags / forecast_layers contracts elsewhere in this file.
  const salesLayerOK =
    forecast_layers?.sales?.can_render !== false &&
    truth_flags?.sales_actionable !== false;
  const isInsufficient = !salesLayerOK;

  // Distinct signal: the *aggregate* decision/forecast grade is INSUFFICIENT
  // (because data quality is low even though the sales math is fine). We
  // still surface this honestly via the inline pill copy — operators get the
  // sales numbers AND the caveat, not one or the other.
  const aggregateGradeInsufficient = confidence?.forecast_grade === 'INSUFFICIENT';

  // P&L Truth Contract gating — derived from parent pnlData.truth_contract
  const truthState = truthContract?.state;
  const truthGate = truthState && truthState !== 'VALID' ? TRUTH_GATE_COPY[truthState] : null;
  const truthSuppress = truthState === 'INVALID' || truthState === 'NOT_ACTIONABLE';

  // Target contract: if the upstream target_state metric is not VALID
  // (e.g. TARGET_NOT_CONFIGURED), we MUST NOT render "Recoverable" /
  // "Behind target" / "At Risk" landing labels or the target gap —
  // recoverable from what? Show sales projection only, with a prompt.
  // Backend also surfaces `landing.target_configured` and
  // `target_gap.target_configured` as a direct signal.
  const targetMetric = truthContract?.metrics?.target_state;
  const targetMetricBlocked =
    targetMetric && targetMetric.state !== 'VALID' && targetMetric.state !== 'DIRECTIONAL';
  const backendSaysNoTarget =
    (landing && landing.target_configured === false) ||
    (target_gap && target_gap.target_configured === false) ||
    landing?.status === 'PROJECTION_ONLY';
  const targetNotConfigured = targetMetricBlocked || backendSaysNoTarget;

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${status.bg}`} data-testid="forecast-card">
      {/* Hero strip */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <span className="text-xs text-zinc-500 font-medium tracking-wide uppercase">
              Operating Forecast
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${grade.bg} ${grade.color}`} data-testid="forecast-grade-badge">
              {grade.label}
            </span>
          </div>
        </div>

        {/* Landing status hero */}
        <div className="flex items-center gap-3 mb-2">
          {targetNotConfigured ? (
            // No target → show a neutral "Projection only" badge. We are
            // forbidden from rendering Recoverable / Behind target / At Risk
            // because those labels imply a target that does not exist.
            <>
              <Target className="w-5 h-5 text-zinc-400" />
              <span
                className="text-lg font-semibold text-zinc-300"
                data-testid="forecast-landing-projection-only"
              >
                Projection only
              </span>
              {!isInsufficient && landing?.projected_net_sales > 0 && (
                <span className="text-zinc-400 text-sm ml-auto">
                  Projected: {fmt(landing.projected_net_sales)}
                </span>
              )}
            </>
          ) : (
            <>
              <StatusIcon className={`w-5 h-5 ${status.color}`} />
              <span className={`text-lg font-semibold ${status.color}`} data-testid="forecast-landing-status">
                {status.label}
              </span>
              {!isInsufficient && landing?.projected_net_sales > 0 && (
                <span className="text-zinc-400 text-sm ml-auto">
                  Projected: {fmt(landing.projected_net_sales)}
                </span>
              )}
            </>
          )}
        </div>

        {/* Target-hit hero line — operationally the single most actionable
            sentence on the page. Promoted from the detail tile so operators
            can read "are we on pace? when do we hit? how close are we?" at
            a glance. Layered-truth gated: only renders when the target IS
            configured AND the sales layer is valid AND the backend has
            actual hit-date math to surface. The detailed tile below remains
            authoritative for the full breakdown. */}
        {!targetNotConfigured && !isInsufficient && (() => {
          const achieved = target_gap?.target_achieved === true;
          const days = target_gap?.days_to_target;
          const hitDate = target_gap?.expected_target_hit_date;
          const daysRemaining = period?.days_remaining ?? 0;
          // Achieved case
          if (achieved) {
            return (
              <div
                className="flex items-center gap-2 mb-2 text-sm"
                data-testid="forecast-hero-target-hit"
              >
                <Target className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-emerald-300 font-medium">Target achieved</span>
                {target_gap?.target_net_sales > 0 && (
                  <span className="text-zinc-500 text-xs">· {fmt(target_gap.target_net_sales)} cleared</span>
                )}
              </div>
            );
          }
          // No usable hit math from backend → don't fabricate; tile below
          // will show "unavailable".
          if (days === null || days === undefined || !hitDate) return null;
          const willHitInPeriod = days <= daysRemaining;
          const dateLabel = (() => {
            try {
              return new Date(hitDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            } catch (_e) {
              return hitDate;
            }
          })();
          return (
            <div
              className="flex items-center gap-2 mb-2 text-sm"
              data-testid="forecast-hero-target-hit"
            >
              <Target className={`w-3.5 h-3.5 shrink-0 ${willHitInPeriod ? 'text-emerald-400' : 'text-amber-400'}`} />
              <span className={`font-medium ${willHitInPeriod ? 'text-emerald-300' : 'text-amber-300'}`}>
                Target Hit: {dateLabel}
              </span>
              <span className="text-zinc-500 text-xs">· {days}d</span>
              {!willHitInPeriod && (
                <span className="text-amber-400/80 text-xs">· beyond month-end</span>
              )}
            </div>
          );
        })()}

        {/* Recommended action — one-liner hero promotion. Pure display of
            the backend field; no logic, no engine, no animation. Hidden
            when sales layer is blocked or backend has nothing to recommend. */}
        {!isInsufficient && recommended_action?.actions?.[0] && (
          <div
            className="flex items-center gap-2 mb-2 text-xs text-zinc-400"
            data-testid="forecast-hero-recommended-action"
            title={recommended_action.title || 'Recommended action'}
          >
            <ArrowRight className="w-3 h-3 text-zinc-500 shrink-0" />
            <span>{recommended_action.actions[0]}</span>
          </div>
        )}

        {/* Narrative — hidden when target narrative would imply a target */}
        {!targetNotConfigured && (
          <p className="text-sm text-zinc-400 leading-relaxed" data-testid="forecast-narrative">
            {landing?.narrative}
          </p>
        )}
        {targetNotConfigured && (
          <p
            className="text-sm text-zinc-400 leading-relaxed"
            data-testid="forecast-target-not-configured"
          >
            Set a monthly target in Settings to enable target tracking, pacing,
            and recovery math. Sales projection is still available below.
          </p>
        )}

        {/* P&L Truth Contract gate — parent-provided truth_contract */}
        {truthGate && (
          <div
            className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${truthGate.tone}`}
            data-testid={`forecast-truth-gate-${truthState?.toLowerCase()}`}
          >
            <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${truthGate.iconTone}`} />
            <div>
              <div className="font-medium">{truthGate.title}</div>
              <div className="opacity-80 mt-0.5">{truthGate.detail}</div>
            </div>
          </div>
        )}

        {/* P0.7 — Confidence gating banners (layered-truth aware).
            When the aggregate grade is INSUFFICIENT, the sales layer may
            still be valid and showing real numbers above this pill — so
            the copy must reflect *what is and isn't* trustworthy, not
            blanket-hide the forecast. */}
        {aggregateGradeInsufficient && salesLayerOK && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md bg-zinc-500/10 border border-zinc-500/20 px-3 py-2 text-xs text-zinc-300"
            data-testid="forecast-gating-layered"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-zinc-400" />
            <span>Sales projection available · profitability confidence still building.</span>
          </div>
        )}
        {aggregateGradeInsufficient && !salesLayerOK && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md bg-zinc-500/10 border border-zinc-500/20 px-3 py-2 text-xs text-zinc-400"
            data-testid="forecast-gating-insufficient"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Insufficient data for reliable forecast</span>
          </div>
        )}
        {confidence?.forecast_grade !== 'INSUFFICIENT' && (
          (confidence?.forecast_grade === 'DIRECTIONAL' || (confidence?.forecast_confidence_score ?? 0) < 60) && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-300" data-testid="forecast-gating-directional">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Directional estimate only — trust the trend, not the exact number.</span>
            </div>
          )
        )}
        {(confidence?.forecast_confidence_score ?? 100) < 45 && confidence?.forecast_grade !== 'INSUFFICIENT' && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-300" data-testid="forecast-gating-low-confidence">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Sales projection low-confidence — high recent volatility.</span>
          </div>
        )}

        {/* P0.3 — Blended-forecast explicit strip
            1OS labels: "Remaining" is the projected future sales (the
            operator's question is "what do I still expect to make?"),
            separate from "Remaining to Target" (what still must be cleared
            to hit the number). Required Daily = remaining_to_target /
            days_left, NOT the projected daily rate. */}
        {!isInsufficient && forecast_final !== undefined && (() => {
          const _remainingToTarget = target_gap?.remaining_to_target;
          const _daysToTarget = target_gap?.days_to_target;
          const _hitDate = target_gap?.expected_target_hit_date;
          const _achieved = target_gap?.target_achieved === true;
          const _requiredDailyToTarget =
            target_gap?.required_daily_to_target ??
            target_gap?.required_daily_rate;
          const _hasTarget = target_gap?.target_configured !== false;
          return (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 rounded-md border border-zinc-800/60 bg-zinc-950/40 p-2" data-testid="forecast-blended-strip">
              <div data-testid="forecast-actual-mtd">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Actual MTD</div>
                <div className="text-sm font-semibold text-zinc-200">{fmt(actual_mtd)}</div>
              </div>
              <div data-testid="forecast-projected-future-sales">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Projected Future Sales</div>
                <div className="text-sm font-semibold text-zinc-200">{fmt(projected_remaining)}</div>
                <div className="text-[10px] text-zinc-500">over {period?.days_remaining ?? 0}d</div>
              </div>
              <div data-testid="forecast-final-value">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Final Forecast</div>
                <div className="text-sm font-semibold text-emerald-300">{fmt(forecast_final)}</div>
              </div>
              {_hasTarget ? (
                <>
                  <div data-testid="forecast-remaining-to-target">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Remaining to Target</div>
                    <div className={`text-sm font-semibold ${_achieved ? 'text-emerald-400' : 'text-zinc-200'}`}>
                      {_achieved ? 'Achieved' : fmt(_remainingToTarget)}
                    </div>
                    {!_achieved && target_gap?.target_net_sales > 0 && (
                      <div className="text-[10px] text-zinc-500">of {fmt(target_gap.target_net_sales)}</div>
                    )}
                  </div>
                  <div data-testid="forecast-required-daily-to-target">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Required Daily</div>
                    <div className={`text-sm font-semibold ${_achieved ? 'text-emerald-400' : 'text-zinc-200'}`}>
                      {_achieved ? '—' : fmt(_requiredDailyToTarget)}
                    </div>
                    {!_achieved && historical_daily_max > 0 && (
                      <div className="text-[10px] text-zinc-500">max seen {fmt(historical_daily_max)}</div>
                    )}
                  </div>
                  <div data-testid="forecast-target-hit">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Target Hit</div>
                    {_achieved ? (
                      <div className="text-sm font-semibold text-emerald-400">Achieved</div>
                    ) : _daysToTarget !== null && _daysToTarget !== undefined ? (
                      <>
                        <div className="text-sm font-semibold text-zinc-200">{_daysToTarget}d</div>
                        {_hitDate && (
                          <div className="text-[10px] text-zinc-500">
                            {new Date(_hitDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm font-semibold text-zinc-500">unavailable</div>
                    )}
                  </div>
                </>
              ) : (
                <div className="col-span-3 flex items-center px-2 text-[11px] text-zinc-500" data-testid="forecast-no-target-strip">
                  No monthly target configured — set one in Settings to enable target tracking.
                </div>
              )}
            </div>
          );
        })()}

        {/* P0.3 — Plausibility cap badge */}
        {plausibility?.capped && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 text-[11px] text-blue-300" data-testid="forecast-plausibility-capped">
            <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Capped — <span className="font-medium">{plausibility.reason === 'daily_exceeds_max' ? 'daily exceeds historical max' : 'run-rate divergence'}</span>
            </span>
          </div>
        )}

        {/* Forecast delta */}
        {forecast_delta?.vs_yesterday && forecast_delta.vs_yesterday.net_sales_projection_delta !== 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
            <Activity className="w-3 h-3" />
            <span>{forecast_delta.reason}</span>
          </div>
        )}
      </div>

      {/* Quick stats row */}
      {!isInsufficient && (
        <div className="grid grid-cols-3 gap-px bg-zinc-800/30">
          {targetNotConfigured ? (
            <QuickStat
              label="Target Gap"
              value="—"
              sub="no target set"
              positive={false}
              testId="forecast-target-gap"
            />
          ) : (
            <QuickStat
              label="Target Gap"
              value={target_gap?.gap_pct !== undefined ? `${target_gap.gap_pct > 0 ? '+' : ''}${target_gap.gap_pct}%` : '--'}
              sub={target_gap?.gap_amount ? fmt(target_gap.gap_amount) : ''}
              positive={target_gap?.gap_pct >= 0}
              testId="forecast-target-gap"
            />
          )}
          {!cogsActionable ? (
            <QuickStat
              label="Gross Margin"
              value="—"
              sub="COGS not decision-grade"
              positive={false}
              testId="forecast-gross-margin"
            />
          ) : (
            <QuickStat
              label="Gross Margin"
              value={margin_layer?.gross_margin_pct ? `${margin_layer.gross_margin_pct}%` : (landing?.projected_gross_margin_pct ? `${landing.projected_gross_margin_pct}%` : '--')}
              sub={vs_last_month?.net_sales_delta_pct ? `${vs_last_month.net_sales_delta_pct > 0 ? '+' : ''}${vs_last_month.net_sales_delta_pct}% vs LM` : ''}
              positive={(margin_layer?.gross_margin_pct || landing?.projected_gross_margin_pct || 0) >= 60}
              testId="forecast-gross-margin"
            />
          )}
          {cashActionable ? (
            <QuickStat
              label="Cash Runway"
              value={cash_layer?.runway_days !== undefined ? `${cash_layer.runway_days}d` : (cash_signal?.runway_days !== undefined ? `${cash_signal.runway_days}d` : '--')}
              sub={cash_signal?.note?.split('.')[0]}
              positive={cash_signal?.risk === 'LOW'}
              testId="forecast-cash-runway"
            />
          ) : (
            <QuickStat
              label="Cash Runway"
              value="—"
              sub="opening not configured"
              positive={false}
              testId="forecast-cash-runway"
            />
          )}
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full py-2 flex items-center justify-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors bg-zinc-900/40"
        data-testid="forecast-expand-toggle"
      >
        {expanded ? 'Less detail' : 'Full forecast'} 
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Expanded detail */}
      {expanded && !isInsufficient && (
        <div className="p-4 pt-2 space-y-4 bg-zinc-950/40 border-t border-zinc-800/40">

          {/* Cost-side forecast lock — driven by backend truth_flags, not
              the parent truth_contract alone. Cost/margin lock when COGS
              is not actionable; cash locks independently when opening
              balance is unknown. */}
          {!cogsActionable && (
            <div
              className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/8 text-rose-200 px-3 py-2 text-xs"
              data-testid="forecast-cost-layers-suppressed"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-400" />
              <div>
                <div className="font-medium">Cost-side forecast locked</div>
                <div className="opacity-80 mt-0.5">
                  Inventory/COGS not decision-grade — expense and margin forecasts suppressed.
                </div>
              </div>
            </div>
          )}
          {!cashActionable && (
            <div
              className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-xs"
              data-testid="forecast-cash-layer-suppressed"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
              <div>
                <div className="font-medium">Cash forecast locked</div>
                <div className="opacity-80 mt-0.5">
                  Opening balance not configured — runway and cash projections suppressed.
                </div>
              </div>
            </div>
          )}

          {/* Layer Explanations */}
          {explanations?.length > 0 && (
            <DetailSection icon={MessageSquare} title="Why These Numbers" testId="forecast-explanations-section">
              <div className="space-y-2">
                {explanations.map((e) => (
                  <div
                    key={e.layer}
                    className={`flex items-start gap-2 text-sm ${e.suppressed ? 'opacity-70' : ''}`}
                    data-testid={`forecast-explanation-${(e.layer || '').toLowerCase()}${e.suppressed ? '-suppressed' : ''}`}
                  >
                    <LayerBadge layer={e.layer} />
                    <span className={e.suppressed ? 'text-zinc-500 italic' : 'text-zinc-400'}>
                      {e.narrative}
                    </span>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Expense Layer — gated on cogs_actionable */}
          {cogsActionable && expense_layer && (
            <DetailSection icon={BarChart3} title="Expense Forecast" testId="forecast-expense-layer-section">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500 text-xs">COGS (EOM)</span>
                  <p className="text-zinc-300 font-medium">{fmt(expense_layer.cogs?.eom_projected)}</p>
                  <span className="text-zinc-600 text-xs">{fmt(expense_layer.cogs?.daily_rate)}/day</span>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs">OPEX (EOM)</span>
                  <p className="text-zinc-300 font-medium">{fmt(expense_layer.opex?.eom_projected)}</p>
                  <span className="text-zinc-600 text-xs">{fmt(expense_layer.opex?.daily_rate)}/day</span>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs">Total Expense</span>
                  <p className="text-zinc-300 font-medium">{fmt(expense_layer.total_expense?.eom_projected)}</p>
                  <span className="text-zinc-600 text-xs">{fmt(expense_layer.total_expense?.daily_rate)}/day</span>
                </div>
              </div>
              {expense_layer.cogs?.breakdown && (
                <div className="mt-2 flex items-center gap-3 text-xs text-zinc-600">
                  <span>Food: {fmt(expense_layer.cogs.breakdown.food?.eom)}</span>
                  <span>Liquor: {fmt(expense_layer.cogs.breakdown.liquor?.eom)}</span>
                  <span>Beverage: {fmt(expense_layer.cogs.breakdown.beverage?.eom)}</span>
                </div>
              )}
            </DetailSection>
          )}

          {/* Margin Layer — gated on cogs_actionable */}
          {cogsActionable && margin_layer && (
            <DetailSection icon={PieChart} title="Margin Projection" testId="forecast-margin-layer-section">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500 text-xs">Gross Margin</span>
                  <p className={`font-semibold ${margin_layer.gross_margin_pct >= 60 ? 'text-emerald-400' : margin_layer.gross_margin_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {margin_layer.gross_margin_pct}%
                  </p>
                  <span className="text-zinc-600 text-xs">{fmt(margin_layer.gross_profit)} profit</span>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs">Net Margin</span>
                  <p className={`font-semibold ${margin_layer.net_margin_pct >= 15 ? 'text-emerald-400' : margin_layer.net_margin_pct >= 8 ? 'text-amber-400' : 'text-red-400'}`}>
                    {margin_layer.net_margin_pct}%
                  </p>
                  <span className="text-zinc-600 text-xs">{fmt(margin_layer.net_profit)} profit</span>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-zinc-600">
                <span>COGS: {margin_layer.cogs_pct}%</span>
                <span>OPEX: {margin_layer.opex_pct}%</span>
                <span>Revenue: {fmt(margin_layer.projected_revenue)}</span>
              </div>
            </DetailSection>
          )}

          {/* Cash Layer — gated on cash_actionable */}
          {cashActionable && cash_layer && (
            <DetailSection icon={DollarSign} title="Cash Forecast" testId="forecast-cash-layer-section">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500 text-xs flex items-center gap-1">
                    <ArrowUp className="w-3 h-3 text-emerald-400" /> Inflows
                  </span>
                  <p className="text-zinc-300 font-medium">{fmt(cash_layer.inflows?.eom_projected)}</p>
                  <span className="text-zinc-600 text-xs">{fmt(cash_layer.inflows?.daily_rate)}/day</span>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs flex items-center gap-1">
                    <ArrowDown className="w-3 h-3 text-red-400" /> Outflows
                  </span>
                  <p className="text-zinc-300 font-medium">{fmt(cash_layer.outflows?.eom_projected)}</p>
                  <span className="text-zinc-600 text-xs">{fmt(cash_layer.outflows?.daily_rate)}/day</span>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs">Net Cash</span>
                  <p className={`font-medium ${cash_layer.net_cash_flow?.eom_projected >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fmt(cash_layer.net_cash_flow?.eom_projected)}
                  </p>
                  <span className="text-zinc-600 text-xs">{cash_layer.runway_days}d runway</span>
                </div>
              </div>
            </DetailSection>
          )}

          {/* Recovery — requires a target */}
          {!targetNotConfigured && target_gap?.gap_pct < 0 && (
            <DetailSection icon={Target} title="Recovery Math" testId="forecast-recovery-section">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500 text-xs">Required/day</span>
                  <p className="text-zinc-300 font-medium">{fmt(recovery?.required_daily)}</p>
                </div>
                <div>
                  <span className="text-zinc-500 text-xs">Best historical/day</span>
                  <p className="text-zinc-300 font-medium">{fmt(recovery?.historical_max_daily)}</p>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-2">{recovery?.narrative}</p>
              <FeasibilityBadge feasibility={recovery?.feasibility} />
            </DetailSection>
          )}

          {/* Short horizon */}
          <DetailSection icon={Clock} title="Short Horizon" testId="forecast-short-horizon-section">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-zinc-500 text-xs">Next 3 days</span>
                <p className="text-zinc-300 font-medium">{fmt(short_horizon?.next_3_days_projection)}</p>
              </div>
              <div>
                <span className="text-zinc-500 text-xs">Next 7 days</span>
                <p className="text-zinc-300 font-medium">{fmt(short_horizon?.next_7_days_projection)}</p>
              </div>
              <div>
                <span className="text-zinc-500 text-xs">Weekend</span>
                <p className="text-zinc-300 font-medium">{fmt(short_horizon?.weekend_projection)}</p>
              </div>
            </div>
            {short_horizon?.risk_window && (
              <p className="text-xs text-amber-400/80 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {short_horizon.risk_window}
              </p>
            )}
            {short_horizon?.opportunity_window && (
              <p className="text-xs text-emerald-400/80 mt-1 flex items-center gap-1">
                <Zap className="w-3 h-3" /> {short_horizon.opportunity_window}
              </p>
            )}
          </DetailSection>

          {/* Revenue mix */}
          <DetailSection icon={BarChart3} title="Revenue Mix" testId="forecast-mix-section">
            <MixBar mix={mix_forecast} />
            {mix_forecast?.mix_risk && (
              <p className="text-xs text-amber-400/80 mt-2">{mix_forecast.mix_risk}</p>
            )}
          </DetailSection>

          {/* Drivers */}
          {drivers?.length > 0 && (
            <DetailSection icon={Activity} title="Forecast Drivers" testId="forecast-drivers-section">
              <div className="space-y-1.5">
                {drivers.map((d, i) => (
                  <div key={d.title || `driver-${i}`} className="flex items-start gap-2 text-sm">
                    <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${d.type === 'RISK' ? 'bg-red-400' : 'bg-emerald-400'}`} />
                    <span className="text-zinc-400">{d.title}</span>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Recommended action */}
          <DetailSection icon={Zap} title="Recommended Action" testId="forecast-action-section">
            <p className="text-sm text-zinc-300 font-medium mb-2">{recommended_action?.title}</p>
            <div className="space-y-1">
              {recommended_action?.actions?.map((a, i) => (
                <div key={a.label || a || `action-${i}`} className="flex items-start gap-2 text-sm text-zinc-400">
                  <ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-blue-400" />
                  <span>{a}</span>
                </div>
              ))}
            </div>
            {recommended_action?.expected_impact && (
              <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
                {recommended_action.expected_impact.margin_recovery_pts > 0 && (
                  <span>+{recommended_action.expected_impact.margin_recovery_pts}pts margin</span>
                )}
                {recommended_action.expected_impact.revenue_uplift > 0 && (
                  <span>+{fmt(recommended_action.expected_impact.revenue_uplift)} revenue</span>
                )}
              </div>
            )}
          </DetailSection>

          {/* Layer Confidence */}
          {layer_confidence && (
            <DetailSection icon={Layers} title="Layer Confidence" testId="forecast-layer-confidence-section">
              <div className="grid grid-cols-4 gap-3 text-sm">
                {['sales', 'expense', 'margin', 'cash'].map(layer => {
                  const lc = layer_confidence[layer];
                  if (!lc) return null;
                  return (
                    <div key={layer}>
                      <span className="text-zinc-500 text-xs capitalize">{layer}</span>
                      <p className={`font-semibold ${confColor(lc.score)}`}>{lc.score}</p>
                      <span className="text-zinc-600 text-xs">{lc.level}</span>
                    </div>
                  );
                })}
              </div>
            </DetailSection>
          )}

          {/* Forecast confidence detail */}
          <DetailSection icon={Shield} title="Forecast Confidence" testId="forecast-confidence-section">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-zinc-500 text-xs">Data confidence</span>
                <p className="text-zinc-300">{confidence?.data_confidence_score}/100 ({confidence?.data_confidence_level})</p>
              </div>
              <div>
                <span className="text-zinc-500 text-xs">Forecast confidence</span>
                <p className="text-zinc-300">{confidence?.forecast_confidence_score}/100 ({confidence?.forecast_confidence_level})</p>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2">{confidence?.forecast_grade_reason}</p>
            <div className="mt-2 flex items-center gap-3 text-xs text-zinc-600">
              <span>{confidence?.weeks_available}w history</span>
              <span>{(confidence?.weekday_coverage * 100).toFixed(0)}% weekday coverage</span>
              <span>{confidence?.clean_data_points} data points</span>
            </div>
          </DetailSection>

          {/* Period */}
          <div className="text-xs text-zinc-600 flex items-center gap-3 pt-2 border-t border-zinc-800/40" data-testid="forecast-period-info">
            <span>{period?.month}</span>
            <span>{period?.days_elapsed}/{period?.days_in_month} days</span>
            <span>{period?.days_remaining} remaining: {period?.remaining_weekdays?.join(', ')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components
const QuickStat = ({ label, value, sub, positive, testId }) => (
  <div className="p-3 bg-zinc-900/60" data-testid={testId}>
    <span className="text-xs text-zinc-500">{label}</span>
    <p className={`text-sm font-semibold ${positive ? 'text-emerald-400' : 'text-amber-400'}`}>{value}</p>
    {sub && <span className="text-xs text-zinc-600">{sub}</span>}
  </div>
);

const DetailSection = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-3.5 h-3.5 text-zinc-500" />
      <span className="text-xs text-zinc-500 font-medium tracking-wide uppercase">{title}</span>
    </div>
    {children}
  </div>
);

const LayerBadge = ({ layer }) => {
  const config = {
    SALES: { bg: 'bg-blue-500/15 text-blue-400', label: 'Sales' },
    EXPENSE: { bg: 'bg-orange-500/15 text-orange-400', label: 'Expense' },
    MARGIN: { bg: 'bg-purple-500/15 text-purple-400', label: 'Margin' },
    CASH: { bg: 'bg-emerald-500/15 text-emerald-400', label: 'Cash' },
  };
  const c = config[layer] || { bg: 'bg-zinc-500/15 text-zinc-400', label: layer };
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${c.bg}`} data-testid={`forecast-layer-badge-${layer?.toLowerCase()}`}>
      {c.label}
    </span>
  );
};

const FeasibilityBadge = ({ feasibility }) => {
  const config = {
    LIKELY: { color: 'text-emerald-400 bg-emerald-500/15', label: 'Recovery likely' },
    POSSIBLE: { color: 'text-amber-400 bg-amber-500/15', label: 'Recovery possible' },
    UNLIKELY: { color: 'text-red-400 bg-red-500/15', label: 'Recovery unlikely' },
    UNREALISTIC: { color: 'text-red-500 bg-red-500/20', label: 'Recovery unrealistic' },
  };
  const c = config[feasibility] || config.UNLIKELY;
  return (
    <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${c.color}`} data-testid="forecast-feasibility-badge">
      {c.label}
    </span>
  );
};

const MixBar = ({ mix }) => {
  if (!mix) return null;
  const food = mix.food_sales?.pct_of_total || 0;
  const alc = mix.alcohol_sales?.pct_of_total || 0;
  const bev = mix.beverage_sales?.pct_of_total || 0;
  
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-zinc-800" data-testid="forecast-mix-bar">
        {food > 0 && <div className="bg-orange-500/80" style={{ width: `${food}%` }} />}
        {alc > 0 && <div className="bg-purple-500/80" style={{ width: `${alc}%` }} />}
        {bev > 0 && <div className="bg-cyan-500/80" style={{ width: `${bev}%` }} />}
      </div>
      <div className="flex items-center gap-4 mt-1.5 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-orange-500/80" /> Food {food.toFixed(0)}%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-purple-500/80" /> Alcohol {alc.toFixed(0)}%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-cyan-500/80" /> Beverage {bev.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

export default ForecastCard;
